import { Group, Text, Badge, Button } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import '../webhooks.css';

export type Subscription = admin.AdminWebhookSubscription;

/** The consecutive-failure streak at which the service disables a subscription. */
export const AUTO_DISABLE_AT = 10;

export function autoDisabled(r: Subscription): boolean {
  return !r.is_active && r.failure_count >= AUTO_DISABLE_AT;
}

/**
 * An operator pausing an endpoint and the service killing one after ten
 * straight failures are different events. Rendering both as a red "down" badge
 * made them indistinguishable, so a deliberate pause looked like a fault and a
 * fault looked deliberate.
 */
export function StateBadge({ r, t }: { r: Subscription; t: Translate }) {
  if (r.is_active) return <StatusBadge status="healthy">{t('status.active')}</StatusBadge>;
  if (autoDisabled(r)) return <StatusBadge status="down">{t('webhooks.autoDisabled')}</StatusBadge>;
  return <StatusBadge status="inactive">{t('status.disabled')}</StatusBadge>;
}

/** The full URL, copyable, because the long ones are the misconfigured ones. */
export function UrlCell({ url, maxWidth }: { url: string; maxWidth?: number }) {
  const t = useT();
  async function copy(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      notifySuccess(t('common.copied'));
    } catch {
      // A denied clipboard is not worth a toast; the tooltip still shows it.
    }
  }
  return (
    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
      <Tooltip label={url} position="top" maxWidth={520}>
        <Text role="bodySmall" style={{ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {url}
        </Text>
      </Tooltip>
      <Button size="xs" variant="subtle" onClick={copy} aria-label={t('webhooks.copyUrl')}>{t('action.copy')}</Button>
    </Group>
  );
}

/**
 * The streak read against the limit it is racing towards.
 *
 * A subscription at eight failures is one bad deploy from being killed and used
 * to look exactly like one at one. The bar answers "how close" without the
 * operator having to remember the threshold.
 */
export function FailureMeter({ r, t }: { r: Subscription; t: Translate }) {
  const pct = Math.min(100, (r.failure_count / AUTO_DISABLE_AT) * 100);
  const tone = r.failure_count === 0
    ? 'var(--rr-color-text-muted)'
    : r.failure_count >= AUTO_DISABLE_AT
      ? 'var(--rr-color-error)'
      : r.failure_count >= AUTO_DISABLE_AT - 3
        ? 'var(--rr-color-error)'
        : 'var(--rr-color-warning)';

  return (
    <Tooltip label={t('webhooks.streakTooltip', { threshold: AUTO_DISABLE_AT })} position="top">
      <span
        className="rr-wh-meter"
        style={{ ['--rr-wh-meter-tone' as string]: tone }}
        role="img"
        aria-label={t('webhooks.meter.label', { count: r.failure_count, threshold: AUTO_DISABLE_AT })}
      >
        <span className="rr-wh-meter__figure">
          {t('webhooks.streakOf', { count: r.failure_count, threshold: AUTO_DISABLE_AT })}
        </span>
        <span className="rr-wh-meter__track">
          <span className="rr-wh-meter__fill" style={{ width: `${Math.max(pct, r.failure_count > 0 ? 6 : 0)}%` }} />
        </span>
      </span>
    </Tooltip>
  );
}

export function EventChips({ events, max }: { events: string[]; max: number }) {
  const t = useT();
  const shown = events.slice(0, max);
  const rest = events.slice(max);
  return (
    <Group gap={4} wrap="wrap">
      {shown.map((e) => <Badge key={e} size="xs" variant="light">{e}</Badge>)}
      {rest.length > 0 && (
        <Tooltip label={rest.join(', ')} position="top" maxWidth={360}>
          <Badge size="xs" variant="light" color="gray">{t('webhooks.moreEvents', { count: rest.length })}</Badge>
        </Tooltip>
      )}
    </Group>
  );
}
